import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    Grid,
    styled,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import * as Yup from 'yup';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';

import logger from 'lib/logger';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Location } from 'components/location/models/LocationModel';
import { ButtonGroupInputFormField } from 'lib/form/fields/ButtonGroupInputFormField';
import { MuiForm } from 'lib/Form';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { Form, FormikHelpers } from 'formik';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { getMinutesToExpiryComponents, getTimeUnitsToMinutes, TimeMeasure } from 'lib/timeHelpers';
import { cooldownMeasureOptions, FormDirtyObserver, minimumRatingOptions } from './ReviewsGlobalSettings';
import { Option } from 'lib/types';
import { parseIntOrNull } from 'lib/helpers';
import { isEmptyString } from 'lib/typeguards';

const GoogleReviewsInstructions = React.forwardRef((props, ref) => (
    <Box {...props} ref={ref}>
        <Typography variant="caption">How to find your Google review link:</Typography>
        <ol style={{ listStyleType: 'decimal', paddingLeft: 12, gap: 4 }}>
            <li>
                Go to{' '}
                <a href="https://google.com/business" target="_blank" rel="noopener noreferrer">
                    google.com/business
                </a>{' '}
                and sign in
            </li>
            <li>Select your restaurant location</li>
            <li>Click &quot;Get more reviews&quot; or &quot;Share review form&quot;</li>
            <li>Copy the link.</li>
        </ol>
    </Box>
));

const collectRatingsOptions: Option[] = [
    {
        label: 'Default',
        value: null
    },
    {
        label: 'On',
        value: 'on'
    },
    {
        label: 'Off',
        value: 'off'
    }
];

const cooldownOptions: Option[] = [
    {
        label: 'Default',
        value: null
    },
    {
        label: 'Custom',
        value: 'custom'
    }
];

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[0],
    overflow: 'hidden'
}));

const StyledLoadingBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    opacity: 0.7,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2)
}));

export const StyledButtonWrapper = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    position: 'sticky',
    bottom: 0,
    backdropFilter: 'blur(25px)',
    padding: theme.spacing(2),
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2)
}));

const StyledAccordionSummary = styled(AccordionSummary, { shouldForwardProp: prop => prop !== 'expand' })<{
    expand: boolean;
}>(({ theme, expand }) => ({
    borderBottom: expand ? `1px solid ${theme.palette.divider}` : 'none',
    backgroundColor: theme.palette.grey[100]
}));

export const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
    '&.Mui-disabled': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.action.disabled,
        color: theme.palette.grey[500]
    }
}));

const defaultValues: ReviewsLocationSettingsFormData = {
    selectedLocationId: '',
    googleReviewRedirectUrl: '',
    collectRatings: null,
    googleReviewRedirectMinimumRatingSelect: null,
    googleReviewRedirectMinimumRating: '5',
    cooldownAfterDismissalSelect: null,
    cooldownAfterDismissal: '0',
    cooldownAfterSubmissionSelect: null,
    cooldownAfterDismissalMeasure: TimeMeasure.MINUTE,
    cooldownAfterSubmission: '0',
    cooldownAfterSubmissionMeasure: TimeMeasure.MINUTE
};

const validationSchema = Yup.object().shape({
    googleReviewRedirectUrl: Yup.string().url('Please enter a valid URL.').notRequired(),
    cooldownAfterDismissal: Yup.mixed().when('cooldownAfterDismissalSelect', {
        is: (value: 'custom') => value === 'custom',
        then: Yup.number()
            .required('This field is required.')
            .min(0, 'This field must be a positive number.'),
        otherwise: Yup.string().notRequired()
    }),
    cooldownAfterDismissalMeasure: Yup.mixed().when('cooldownAfterDismissalSelect', {
        is: (value: 'custom') => value === 'custom',
        then: Yup.string().required('This field is required.'),
        otherwise: Yup.string().notRequired()
    }),
    cooldownAfterSubmission: Yup.mixed().when('cooldownAfterSubmissionSelect', {
        is: (value: 'custom') => value === 'custom',
        then: Yup.number()
            .required('This field is required.')
            .min(0, 'This field must be a positive number.'),
        otherwise: Yup.string().notRequired()
    }),
    cooldownAfterSubmissionMeasure: Yup.mixed().when('cooldownAfterSubmissionSelect', {
        is: (value: 'custom') => value === 'custom',
        then: Yup.string().required('This field is required.'),
        otherwise: Yup.string().notRequired()
    })
});

interface ReviewsLocationSettingsFormData {
    selectedLocationId: string;
    googleReviewRedirectUrl: string;
    googleReviewRedirectMinimumRatingSelect: 'custom' | null;
    googleReviewRedirectMinimumRating: string | null;
    cooldownAfterDismissalSelect: 'custom' | null;
    cooldownAfterDismissal: string;
    cooldownAfterDismissalMeasure: TimeMeasure;
    cooldownAfterSubmissionSelect: 'custom' | null;
    cooldownAfterSubmission: string;
    cooldownAfterSubmissionMeasure: TimeMeasure;
    collectRatings: 'on' | 'off' | null;
}

interface ReviewsLocationSettingsProps {
    locations: Location[];
    isLoadingLocations: boolean;
    onSave: (settings: Partial<IPublicAndPrivateSettings>, locationId: string) => Promise<void>;
    submitRef?: React.MutableRefObject<(() => void) | null>;
    isSaving: boolean;
    onDirtyChange?: (dirty: boolean) => void;
    onValidityChange?: (isValid: boolean) => void;
    hasReviewsWritePermission: boolean;
}
export const ReviewsLocationSettings = ({
    locations,
    isLoadingLocations,
    submitRef,
    isSaving,
    onSave,
    onDirtyChange,
    onValidityChange,
    hasReviewsWritePermission
}: ReviewsLocationSettingsProps) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const [expanded, setExpanded] = React.useState(false);
    const [isSettingsLoading, setIsSettingsLoading] = React.useState(false);
    const [isFormikDirty, setIsFormikDirty] = React.useState(false);

    const firstSelectableLocationId = React.useMemo(
        () =>
            locations.find(location => location.state === 'ACTIVE' || location.state === 'PENDING')?._id ??
            '',
        [locations]
    );

    const [initialValues, setInitialValues] = React.useState<ReviewsLocationSettingsFormData>({
        selectedLocationId: firstSelectableLocationId,
        ...defaultValues
    });

    const locationOptions = React.useMemo(() => {
        if (!Array.isArray(locations) || isLoadingLocations) {
            return [];
        }
        return locations
            .filter(location => location.state === 'ACTIVE' || location.state === 'PENDING')
            .map(location => ({
                label: location.title,
                value: location._id
            }));
    }, [locations, isLoadingLocations]);

    const handleAccordionChange = React.useCallback((_: React.SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded);
    }, []);

    const handleLocationSelectChange = React.useCallback(
        async (value: string | string[]) => {
            if (Array.isArray(value)) {
                value = value[0];
            }
            try {
                setIsSettingsLoading(true);
                const settings = await new LocationSettingsApi().getPublicSettings(null, value, true);
                const cooldownAfterDismissal = getMinutesToExpiryComponents(
                    settings.prompts?.cooldownMins?.onDismissed ?? 0
                );
                const cooldownAfterSubmission = getMinutesToExpiryComponents(
                    settings.prompts?.cooldownMins?.onCompleted ?? 0
                );
                let collectRatings: 'on' | 'off' | null;
                if (
                    settings.reviewsEnabledByMerchant === null ||
                    settings.reviewsEnabledByMerchant === undefined
                ) {
                    collectRatings = null;
                } else {
                    collectRatings = settings.reviewsEnabledByMerchant ? 'on' : 'off';
                }
                setInitialValues({
                    selectedLocationId: value ?? '',
                    googleReviewRedirectUrl: settings.reviews?.externalReviewUrl ?? '',
                    googleReviewRedirectMinimumRatingSelect: !!settings.reviews
                        ?.minimumRatingToTriggerExternalReview
                        ? 'custom'
                        : null,
                    googleReviewRedirectMinimumRating:
                        settings.reviews?.minimumRatingToTriggerExternalReview?.toString() ?? null,
                    cooldownAfterDismissalSelect: !!settings.prompts?.cooldownMins?.onDismissed
                        ? 'custom'
                        : null,
                    cooldownAfterDismissal: cooldownAfterDismissal.value.toString(),
                    cooldownAfterDismissalMeasure: cooldownAfterDismissal.measure,
                    cooldownAfterSubmissionSelect: !!settings.prompts?.cooldownMins?.onCompleted
                        ? 'custom'
                        : null,
                    cooldownAfterSubmission: cooldownAfterSubmission.value.toString(),
                    cooldownAfterSubmissionMeasure: cooldownAfterSubmission.measure,
                    collectRatings
                });
            } catch (error) {
                logger.error(`Error Loading Location Settings: ${JSON.stringify(error)}`);
                enqueueSnackbar('Failed to load location settings', { variant: 'error' });
            } finally {
                setIsSettingsLoading(false);
            }
        },
        [enqueueSnackbar]
    );

    const handleSave = React.useCallback(
        async (
            values: ReviewsLocationSettingsFormData,
            { resetForm }: FormikHelpers<ReviewsLocationSettingsFormData>
        ) => {
            const cooldownAfterDismissalMins = getTimeUnitsToMinutes(
                parseIntOrNull(values.cooldownAfterDismissal),
                values.cooldownAfterDismissalMeasure
            );
            const cooldownAfterDismissalType = values.cooldownAfterDismissalSelect;
            const cooldownAfterSubmissionMins = getTimeUnitsToMinutes(
                parseIntOrNull(values.cooldownAfterSubmission),
                values.cooldownAfterSubmissionMeasure
            );
            const cooldownAfterSubmissionType = values.cooldownAfterSubmissionSelect;
            let reviewsEnabledByMerchant;
            if (values.collectRatings === null || values.collectRatings === undefined) {
                reviewsEnabledByMerchant = null;
            } else {
                reviewsEnabledByMerchant = values.collectRatings === 'on';
            }
            const settingsToUpdate: IPublicAndPrivateSettings = {
                reviewsEnabledByMerchant,
                reviews: {
                    minimumRatingToTriggerExternalReview:
                        values.googleReviewRedirectMinimumRatingSelect === 'custom'
                            ? parseIntOrNull(values.googleReviewRedirectMinimumRating)
                            : null,
                    externalReviewUrl: values.googleReviewRedirectUrl
                },
                prompts: {
                    cooldownMins: {
                        onDismissed:
                            cooldownAfterDismissalType === 'custom' ? cooldownAfterDismissalMins : null,
                        onCompleted:
                            cooldownAfterSubmissionType === 'custom' ? cooldownAfterSubmissionMins : null
                    }
                }
            };
            await onSave(settingsToUpdate, values.selectedLocationId);
            resetForm({ values });
        },
        [onSave]
    );

    React.useEffect(() => {
        if (firstSelectableLocationId) {
            handleLocationSelectChange(firstSelectableLocationId);
        }
    }, [firstSelectableLocationId, handleLocationSelectChange]);

    React.useEffect(() => {
        onDirtyChange?.(isFormikDirty);
    }, [isFormikDirty, onDirtyChange]);

    return (
        <MuiForm<ReviewsLocationSettingsFormData>
            onSubmit={handleSave}
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {({ submitForm, isValid, values, dirty }) => {
                if (submitRef) {
                    submitRef.current = submitForm;
                }

                return (
                    <Form>
                        <FormDirtyObserver onChange={setIsFormikDirty} onValidityChange={onValidityChange} />
                        <Grid
                            container
                            spacing={2}
                            paddingTop={2}
                            sx={{ minHeight: '100%', position: 'relative' }}
                        >
                            {isSettingsLoading && (
                                <StyledLoadingBox>
                                    <CircularProgress />
                                </StyledLoadingBox>
                            )}
                            <Grid size={12}>
                                <SelectFormField
                                    name="selectedLocationId"
                                    variant="outlined"
                                    onSelect={handleLocationSelectChange}
                                    label={<MuiFormLabel>Location</MuiFormLabel>}
                                    options={locationOptions}
                                    disabled={isSettingsLoading}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                    <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" fontWeight="medium">
                                            Google Review Link
                                        </Typography>
                                        <Tooltip title={<GoogleReviewsInstructions />}>
                                            <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                        </Tooltip>
                                    </Box>
                                    <TextFormField
                                        name="googleReviewRedirectUrl"
                                        variant="outlined"
                                        placeholder="https://search.google.com/local/writereview?placeid=..."
                                        disabled={isSettingsLoading || !hasReviewsWritePermission}
                                    />
                                    {isEmptyString(values.googleReviewRedirectUrl) && (
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            gap={1}
                                            alignItems="center"
                                            sx={{
                                                backgroundColor: theme.palette.grey[100],
                                                padding: 1,
                                                borderRadius: 1
                                            }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: '1.25rem' }} />
                                            <Typography variant="body2">
                                                No Google URL set — all reviews for this location will be kept
                                                <b> in-house</b> and will not redirect customers to Google.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={12}>
                                <StyledAccordion expanded={expanded} onChange={handleAccordionChange}>
                                    <StyledAccordionSummary expand={expanded} expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="body1" fontWeight="medium">
                                            Location Overrides
                                        </Typography>
                                    </StyledAccordionSummary>
                                    <AccordionDetails sx={{ border: 'none', py: 2 }}>
                                        <Box display="flex" flexDirection="column" gap={3}>
                                            <Box display="flex" flexDirection="column">
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    <Typography>Collect Ratings </Typography>
                                                    <Tooltip
                                                        title="Override the default rating collection setting for this location only.
                                            Changes made in the Default Settings tab will not impact this override once it has been set."
                                                    >
                                                        <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </Tooltip>
                                                </Box>
                                                <Box>
                                                    <ButtonGroupInputFormField
                                                        name="collectRatings"
                                                        options={collectRatingsOptions}
                                                        disabled={
                                                            isSettingsLoading || !hasReviewsWritePermission
                                                        }
                                                    />
                                                </Box>
                                            </Box>
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    <Typography variant="body1">
                                                        Minimum Rating for Google Redirect
                                                    </Typography>
                                                    <Tooltip
                                                        title="Override the minimum star rating for Google redirect at this location.
                                        Changes made in the Default Settings tab will not impact this override once it has been set."
                                                    >
                                                        <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </Tooltip>
                                                </Box>
                                                <ButtonGroupInputFormField
                                                    name="googleReviewRedirectMinimumRatingSelect"
                                                    options={cooldownOptions}
                                                    disabled={isSettingsLoading || !hasReviewsWritePermission}
                                                />
                                                {values.googleReviewRedirectMinimumRatingSelect ===
                                                    'custom' && (
                                                    <SelectFormField
                                                        name="googleReviewRedirectMinimumRating"
                                                        options={minimumRatingOptions}
                                                        variant="outlined"
                                                        disabled={
                                                            isSettingsLoading || !hasReviewsWritePermission
                                                        }
                                                        size="small"
                                                    />
                                                )}
                                            </Box>
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    <Typography variant="body1">
                                                        Cooldown After Dismissal
                                                    </Typography>
                                                    <Tooltip
                                                        title="Override the dismissal cooldown for this location only.
                                        Changes made in the Default Settings tab will not impact this override once it has been set."
                                                    >
                                                        <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </Tooltip>
                                                </Box>
                                                <ButtonGroupInputFormField
                                                    name="cooldownAfterDismissalSelect"
                                                    options={cooldownOptions}
                                                    disabled={isSettingsLoading || !hasReviewsWritePermission}
                                                />
                                                {values.cooldownAfterDismissalSelect === 'custom' && (
                                                    <Grid container spacing={1}>
                                                        <Grid size={8}>
                                                            <NumberFormField
                                                                name="cooldownAfterDismissal"
                                                                variant="outlined"
                                                                disabled={
                                                                    isSettingsLoading ||
                                                                    !hasReviewsWritePermission
                                                                }
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <SelectFormField
                                                                name="cooldownAfterDismissalMeasure"
                                                                options={cooldownMeasureOptions}
                                                                variant="outlined"
                                                                disabled={
                                                                    isSettingsLoading ||
                                                                    !hasReviewsWritePermission
                                                                }
                                                                size="small"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Box>
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    gap={0.5}
                                                >
                                                    <Typography variant="body1">
                                                        Cooldown After Submission
                                                    </Typography>
                                                    <Tooltip
                                                        title="Override the submission cooldown for this location only.
                                        Changes made in the Default Settings tab will not impact this override once it has been set."
                                                    >
                                                        <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </Tooltip>
                                                </Box>
                                                <ButtonGroupInputFormField
                                                    name="cooldownAfterSubmissionSelect"
                                                    options={cooldownOptions}
                                                    disabled={isSettingsLoading || !hasReviewsWritePermission}
                                                />
                                                {values.cooldownAfterSubmissionSelect === 'custom' && (
                                                    <Grid container spacing={1}>
                                                        <Grid size={8}>
                                                            <NumberFormField
                                                                name="cooldownAfterSubmission"
                                                                variant="outlined"
                                                                size="small"
                                                                disabled={
                                                                    isSettingsLoading ||
                                                                    !hasReviewsWritePermission
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <SelectFormField
                                                                name="cooldownAfterSubmissionMeasure"
                                                                options={cooldownMeasureOptions}
                                                                variant="outlined"
                                                                disabled={
                                                                    isSettingsLoading ||
                                                                    !hasReviewsWritePermission
                                                                }
                                                                size="small"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Box>
                                        </Box>
                                    </AccordionDetails>
                                </StyledAccordion>
                            </Grid>
                            <Grid size={12} position="sticky" bottom={0}>
                                <StyledButtonWrapper>
                                    <StyledLoadingButton
                                        loading={isSaving}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={submitForm}
                                        disabled={
                                            !isValid ||
                                            isSaving ||
                                            !dirty ||
                                            isSettingsLoading ||
                                            !hasReviewsWritePermission
                                        }
                                    >
                                        Save
                                    </StyledLoadingButton>
                                </StyledButtonWrapper>
                            </Grid>
                        </Grid>
                    </Form>
                );
            }}
        </MuiForm>
    );
};
